import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useTheme } from '@lifeomic/chroma-react/styles';
import { getTestProps } from '../../util/testProps';
import { pagesForExtractionResults } from './memberExtractionResultPages';
import { MemberExtractionResultReview } from './MemberExtractionResultReview';
import type {
  MemberExtractionDocumentGroup,
  MemberExtractionResourceRow,
} from './useMemberExtractionDocuments';
import { useMemberExtractionSummary } from './useMemberExtractionSummary';

const messages = defineMessages({
  labCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.lab',
    defaultMessage: '{count, plural, one {# Lab} other {# Labs}}',
  },
  conditionCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.condition',
    defaultMessage: '{count, plural, one {# Condition} other {# Conditions}}',
  },
  medicationCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.medication',
    defaultMessage: '{count, plural, one {# Medication} other {# Medications}}',
  },
  allergyCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.allergy',
    defaultMessage: '{count, plural, one {# Allergy} other {# Allergies}}',
  },
  immunizationCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.immunization',
    defaultMessage:
      '{count, plural, one {# Immunization} other {# Immunizations}}',
  },
  procedureCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.procedure',
    defaultMessage: '{count, plural, one {# Procedure} other {# Procedures}}',
  },
  otherCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.other',
    defaultMessage: '{count} {type}',
  },
});

const MemberExtractionDocumentPages = React.lazy(() =>
  import('./MemberExtractionDocumentPages').then(module => ({
    default: module.MemberExtractionDocumentPages,
  })),
);

const MemberExtractionDocumentSummary = React.lazy(() =>
  import('./MemberExtractionDocumentSummary').then(module => ({
    default: module.MemberExtractionDocumentSummary,
  })),
);

const stuckPdfGap = 18;
const sectionBorder = '1px solid #b0b0b0';

const bodyStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
  paddingTop: stuckPdfGap,
};

const resultsStyle = {
  flex: '1 1 0',
  minWidth: 160,
  margin: 0,
  paddingLeft: 4,
  listStyle: 'none',
};

const titleRowStyle = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  columnGap: 12,
  rowGap: 4,
  alignItems: 'baseline',
  position: 'sticky' as const,
  top: 0,
  zIndex: 2,
  paddingTop: 8,
  paddingBottom: 4,
};

const documentRowStyle = {
  cursor: 'default' as const,
};

const resourceRowClassName = 'member-extraction-resource-row';

const resultRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  columnGap: 8,
};

const resultColumnStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  flex: '0 1 auto',
  minWidth: 0,
  rowGap: 2,
};

const resultPageColumnStyle = {
  display: 'flex',
  flexDirection: 'row' as const,
  flexWrap: 'nowrap' as const,
  alignItems: 'center',
  flex: '0 0 auto',
  height: '1.375rem',
  gap: 4,
  whiteSpace: 'nowrap' as const,
};

const resultPageChipStyle = {
  minWidth: 18,
  padding: '0 6px',
  border: '2px solid #000',
  borderRadius: 9,
  backgroundColor: '#ffef80',
  color: '#000',
  fontWeight: 700,
  textAlign: 'center' as const,
  lineHeight: '14px',
};

const TYPE_COUNTS: Array<{
  types: string[];
  message: keyof typeof messages;
}> = [
  { types: ['Observation'], message: 'labCount' },
  { types: ['Condition'], message: 'conditionCount' },
  {
    types: ['MedicationRequest', 'MedicationStatement'],
    message: 'medicationCount',
  },
  { types: ['AllergyIntolerance'], message: 'allergyCount' },
  { types: ['Immunization'], message: 'immunizationCount' },
  { types: ['Procedure'], message: 'procedureCount' },
];

const resourceTypeSummary = (
  resources: MemberExtractionResourceRow[],
  formatMessage: (
    message: (typeof messages)[keyof typeof messages],
    values?: Record<string, string | number>,
  ) => string,
) => {
  const counts = new Map<string, number>();
  resources.forEach(resource => {
    counts.set(
      resource.resourceType,
      (counts.get(resource.resourceType) ?? 0) + 1,
    );
  });
  const seen = new Set<string>();
  const parts = TYPE_COUNTS.flatMap(entry => {
    entry.types.forEach(type => seen.add(type));
    const count = entry.types.reduce(
      (sum, type) => sum + (counts.get(type) ?? 0),
      0,
    );
    return count > 0 ? [formatMessage(messages[entry.message], { count })] : [];
  });
  counts.forEach((count, type) => {
    if (!seen.has(type) && count > 0) {
      parts.push(formatMessage(messages.otherCount, { count, type }));
    }
  });
  return parts.join(' · ');
};

type MemberExtractionDocumentRowProps = {
  patientId: string;
  group: MemberExtractionDocumentGroup;
};

export const MemberExtractionDocumentRow: React.FC<
  MemberExtractionDocumentRowProps
> = ({ patientId, group }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const rowRef = React.useRef<HTMLLIElement>(null);
  const titleRef = React.useRef<HTMLDivElement>(null);
  const pdfRef = React.useRef<HTMLDivElement>(null);
  const summaryRef = React.useRef<HTMLDivElement>(null);
  const [titleHeight, setTitleHeight] = React.useState(0);
  const [pdfHeight, setPdfHeight] = React.useState(0);
  const [visible, setVisible] = React.useState(false);
  const [stickSummary, setStickSummary] = React.useState(false);

  React.useEffect(() => {
    const title = titleRef.current;
    if (!title) {
      return;
    }
    const measure = () => setTitleHeight(title.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(title);
    return () => observer.disconnect();
  }, [group.title, group.resources.length]);

  const hasSidePanel = Boolean(group.pdf || group.summaryReference);
  const summary = useMemberExtractionSummary(
    visible ? group.summaryReference : undefined,
  );
  const resultPages = React.useMemo(
    () => pagesForExtractionResults(summary.markdown, group.resources),
    [summary.markdown, group.resources],
  );

  React.useEffect(() => {
    const row = rowRef.current;
    if (!row || !hasSidePanel || visible) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [hasSidePanel, visible]);

  React.useEffect(() => {
    const pdf = pdfRef.current;
    const summary = summaryRef.current;
    if (!pdf && !summary) {
      return;
    }
    const measure = () => {
      const nextPdfHeight = pdf?.offsetHeight ?? 0;
      const summaryHeight = summary?.offsetHeight ?? 0;
      const scrollport =
        rowRef.current?.closest('.member-extraction-page')?.clientHeight ??
        window.innerHeight;
      setPdfHeight(nextPdfHeight);
      setStickSummary(
        nextPdfHeight + summaryHeight <= scrollport - titleHeight - stuckPdfGap,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (pdf) {
      observer.observe(pdf);
    }
    if (summary) {
      observer.observe(summary);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [titleHeight, visible, hasSidePanel]);

  return (
    <li
      ref={rowRef}
      style={documentRowStyle}
      {...getTestProps('member-extraction-document')}
    >
      <style>
        {`.${resourceRowClassName}{cursor:default;border-radius:4px;padding:2px 8px;}.${resourceRowClassName}:hover{background-color:#fff6cc;}`}
      </style>
      <div
        ref={titleRef}
        style={{
          ...titleRowStyle,
          backgroundColor: '#fff',
          borderBottom: sectionBorder,
        }}
      >
        <Text
          size="subbody"
          weight="bold"
          style={{
            color: theme.palette.primary.dark,
            textTransform: 'uppercase',
          }}
        >
          {group.title}
        </Text>
        <Text size="subbody">
          {resourceTypeSummary(group.resources, formatMessage)}
        </Text>
      </div>
      <div style={bodyStyle}>
        <ul style={resultsStyle}>
          {group.resources.map(resource => {
            const pageNumbers =
              resultPages.get(`${resource.resourceType}/${resource.id}`) ?? [];
            return (
              <li
                key={`${resource.resourceType}/${resource.id}`}
                className={resourceRowClassName}
                style={resultRowStyle}
                {...getTestProps('member-extraction-resource')}
              >
                <div style={resultColumnStyle}>
                  <Text size="subbody">{resource.title}</Text>
                  {resource.value ? (
                    <Text
                      size="caption"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {resource.value}
                    </Text>
                  ) : null}
                </div>
                {pageNumbers.length > 0 ? (
                  <div style={resultPageColumnStyle}>
                    {pageNumbers.map(pageNumber => (
                      <Text
                        key={pageNumber}
                        size="caption"
                        style={resultPageChipStyle}
                        {...getTestProps('member-extraction-result-page')}
                      >
                        {pageNumber}
                      </Text>
                    ))}
                  </div>
                ) : null}
                <MemberExtractionResultReview
                  patientId={patientId}
                  resourceType={resource.resourceType}
                  resourceId={resource.id}
                  reviewDecision={resource.reviewDecision}
                />
              </li>
            );
          })}
        </ul>
        {visible && hasSidePanel ? (
          <div
            style={{
              alignSelf: 'stretch',
              flex: '1 1 0',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {group.pdf ? (
              <div
                ref={pdfRef}
                style={{
                  position: 'sticky',
                  top: titleHeight + stuckPdfGap,
                  alignSelf: 'flex-start',
                  width: '100%',
                  zIndex: 1,
                  backgroundColor: '#fff',
                  borderBottom: sectionBorder,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: -stuckPdfGap,
                    right: 0,
                    left: 0,
                    height: stuckPdfGap,
                    backgroundColor: '#fff',
                  }}
                />
                <React.Suspense fallback={null}>
                  <MemberExtractionDocumentPages pdf={group.pdf} />
                </React.Suspense>
              </div>
            ) : null}
            {group.summaryReference ? (
              <div
                ref={summaryRef}
                style={{
                  position: stickSummary ? 'sticky' : 'static',
                  top: titleHeight + stuckPdfGap + pdfHeight,
                }}
              >
                <React.Suspense fallback={null}>
                  <MemberExtractionDocumentSummary
                    markdown={summary.markdown}
                    isLoading={summary.isLoading}
                    failed={summary.failed}
                  />
                </React.Suspense>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
};
